// ConfirmationMailService — the booking confirmation, sent (M2–M3,
// DESK-OPERATOR-AND-MAIL-BRIEF §3).
//
// L2 mirrors L4: prepare / link / send under pms.reservation.write, reads under
// pms.reservation.read. The send is an HTTP call to our own Function
// (`/api/mail/send`, the site project), which holds the Brevo key and claims
// the row as the caller — the `HttpChannelOpsService` pattern.

import { CAPABILITIES, type ActorSource } from '@lemontaigu/platform';
import {
  cardLinkProblem,
  confirmationDraftProblem,
  err,
  ok,
  type ConfirmationMail,
  type ConfirmationMailDispatch,
  type ConfirmationMailDraft,
  type ConfirmationMailRepository,
  type ConfirmationMailSendResult,
  type ConfirmationMailService,
  type ConfirmationMailStatus,
  type Result,
} from '@lemontaigu/pms-core';
import type { ChannelOpsConfig } from './channelOps.js';
import { gate } from './services.js';

export class ConfirmationMailServiceImpl implements ConfirmationMailService {
  constructor(
    private readonly repo: ConfirmationMailRepository,
    private readonly dispatch: ConfirmationMailDispatch,
    private readonly actor: ActorSource,
  ) {}

  async prepare(draft: ConfirmationMailDraft): Promise<Result<string>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RESERVATION_WRITE);
    if (denied) return denied;
    const problem = confirmationDraftProblem(draft);
    if (problem !== null) return err('validation', problem, { retryable: false });
    return this.repo.prepare(draft);
  }

  async setLink(id: string, link: string): Promise<Result<void>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RESERVATION_WRITE);
    if (denied) return denied;
    const problem = cardLinkProblem(link);
    if (problem !== null) return err('validation', problem, { retryable: false });
    return this.repo.setLink(id, link.trim());
  }

  async send(id: string): Promise<Result<ConfirmationMailSendResult>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RESERVATION_WRITE);
    if (denied) return denied;
    return this.dispatch.send(id);
  }

  async latest(reservationId: string): Promise<Result<ConfirmationMail | null>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RESERVATION_READ);
    if (denied) return denied;
    return this.repo.latest(reservationId);
  }

  async pending(): Promise<Result<ConfirmationMail[]>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RESERVATION_READ);
    if (denied) return denied;
    return this.repo.pending();
  }
}

const STATUSES: readonly ConfirmationMailStatus[] = ['awaiting_link', 'queued', 'sending', 'sent', 'failed'];

/** `/api/mail/send` — a refusal or a failure is an answer with a status, not a broken call. */
export class HttpConfirmationMailDispatch implements ConfirmationMailDispatch {
  constructor(
    private readonly cfg: ChannelOpsConfig,
    private readonly fetchImpl: typeof fetch = (input, init) => fetch(input, init),
  ) {}

  async send(id: string): Promise<Result<ConfirmationMailSendResult>> {
    const token = await this.cfg.accessToken();
    if (!token) return err('forbidden', 'Session expirée — reconnectez-vous.', { retryable: false });
    let res: Response;
    try {
      res = await this.fetchImpl(`${this.cfg.apiBase}/api/mail/send`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ outboxId: id }),
      });
    } catch (e) {
      return err('transient', `Envoi injoignable — ${this.cfg.apiBase}`, { retryable: true, cause: e });
    }
    const body = (await res.json().catch(() => null)) as { ok?: boolean; status?: string; reason?: string } | null;
    const status = STATUSES.find((s) => s === body?.status);
    if (!body || status === undefined) return err('integration', `Réponse illisible (${res.status})`, { retryable: res.status >= 500 });
    return ok({ sent: body.ok === true && status === 'sent', status, reason: body.reason ?? null });
  }
}

/** No Function wired (a test harness): the desk is told, nothing is half-done. */
export class UnavailableConfirmationMailDispatch implements ConfirmationMailDispatch {
  async send(): Promise<Result<ConfirmationMailSendResult>> {
    return err('integration', 'Envoi des e-mails non configuré sur cette application.', { retryable: false });
  }
}
